import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './Button';
import { Icon, TONE_ICON } from './Icon';
import { TONE_CLASSES } from '../../lib/domain';

/* ============================================================ modal ======= */

export function Modal({ open, onClose, title, description, children, footer, width = 'md' }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Stop the page behind from scrolling while a modal is up.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  const widths = { sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-2xl' };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`animate-enter relative w-full ${widths[width]} rounded-xl border border-line bg-surface shadow-card`}
      >
        <div className="flex items-start justify-between gap-4 border-b border-line px-4 py-3">
          <div>
            <h2 className="text-[15px] font-semibold text-ink">{title}</h2>
            {description && <p className="mt-0.5 text-[13px] text-ink-3">{description}</p>}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <Icon name="close" size="sm" />
          </Button>
        </div>
        <div className="px-4 py-4">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 border-t border-line px-4 py-3">{footer}</div>
        )}
      </div>
    </div>,
    document.body
  );
}

/* ============================================================ toasts ====== */

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (message, { tone = 'good', duration = 4500 } = {}) => {
      const id = Math.random().toString(36).slice(2);
      setToasts((current) => [...current, { id, message, tone }]);
      if (duration) setTimeout(() => dismiss(id), duration);
      return id;
    },
    [dismiss]
  );

  const value = useMemo(
    () => ({
      push,
      dismiss,
      success: (message) => push(message, { tone: 'good' }),
      error: (message) => push(message, { tone: 'critical', duration: 7000 }),
      info: (message) => push(message, { tone: 'brand' }),
    }),
    [push, dismiss]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-80 flex-col gap-2">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              role="status"
              className={`animate-enter pointer-events-auto flex items-start gap-2 rounded-lg border px-3 py-2.5 shadow-card ${
                TONE_CLASSES[toast.tone] || TONE_CLASSES.neutral
              }`}
            >
              <Icon name={TONE_ICON[toast.tone]} size="sm" className="mt-0.5" />
              <p className="flex-1 text-[13px] leading-snug">{toast.message}</p>
              <button
                type="button"
                onClick={() => dismiss(toast.id)}
                className="opacity-60 hover:opacity-100"
                aria-label="Dismiss"
              >
                <Icon name="close" size="xs" />
              </button>
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside <ToastProvider>');
  return context;
}

/* ============================================================== tabs ====== */

export function Tabs({ tabs, value, onChange, className = '' }) {
  return (
    <div className={`flex items-center gap-1 border-b border-line ${className}`} role="tablist">
      {tabs.map((tab) => {
        const active = tab.value === value;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.value)}
            className={[
              'relative -mb-px flex items-center gap-1.5 px-3 py-2 text-[13.5px] font-medium transition-colors',
              active
                ? 'text-ink after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:rounded-full after:bg-brand'
                : 'text-ink-3 hover:text-ink-2',
            ].join(' ')}
          >
            {tab.icon && <Icon name={tab.icon} size="sm" />}
            {tab.label}
            {tab.count !== undefined && (
              <span className="ml-0.5 rounded-md bg-surface-2 px-1.5 text-[11px] tabular-nums text-ink-3">
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
